import {Injectable, Injector} from '@angular/core';
import {SdkBase} from '../utilities/sdk-base/sdk.base';
import {map, Observable, of} from 'rxjs';
import {BriefcaseCollection} from '../models/ui/briefcase-collection.model';
import {BriefcaseJobResourceToBriefcaseJobMapper} from '../models/mappers/briefcase-job-resource-to-briefcase-job.mapper';
import {Briefcase} from '../models/ui/briefcase.model';
import {BriefcaseToBriefcaseJobResourceMapper} from '../models/mappers/briefcase-to-briefcase-job-resource.mapper';
import {PublicFileResourceToFileMapper} from '../models/mappers/public-file-resource-to-file.mapper';
import {FileRecord, FileType} from '../models/ui/file.model';
import {GetBriefcaseFilesResponseResource} from '../models/resource/get-briefcase-files-response.resource';
import {FileToDownloadBriefcaseFileRequestResourceMapper} from '../models/mappers/file-to-download-briefcase-file-request-resource.mapper';
import {FileDownloadDetailsResource} from '../models/resource/file-download-details.resource';
import {DownloadBriefcaseFileRequestResource} from '../models/resource/download-briefcase-file-request.resource';
import {FileTypeToFileTypeResourceMapper} from '../models/mappers/file-type-to-file-type-resource.mapper';
import {BriefcaseJob} from "@models/review/briefcase-job.model";
import {orderBy} from 'lodash-es';

const briefcaseJobResourceToBriefcaseJobMapper = new BriefcaseJobResourceToBriefcaseJobMapper();
const briefcaseToBriefcaseJobResourceMapper = new BriefcaseToBriefcaseJobResourceMapper();
const publicFileResourceToFileMapper = new PublicFileResourceToFileMapper();

@Injectable({
	providedIn: 'root'
})
export class BriefcaseJobSdk extends SdkBase {
	constructor(injector: Injector) {
		super(injector);
	}

	public get(params?: BriefcaseGetParams): Observable<BriefcaseCollection> {

		if (params?.id) {
			return this.makeHttpRequest<Briefcase[]>('get', `api2/briefcase/${params.id}`).pipe(
				map((response: any) => {
					const result = new BriefcaseCollection();
					result.items = [briefcaseJobResourceToBriefcaseJobMapper.map(response[0])];
					result.totalItems = response.length
					return result;
				})
			);
		}
		return this.makeHttpRequest<Briefcase[]>('get', `api2/briefcase/all`).pipe(
			map((response: any) => {
				const result = new BriefcaseCollection();
				result.items = briefcaseJobResourceToBriefcaseJobMapper.mapCollection(response);
				result.totalItems = response.length
				result.items = orderBy(result.items, 'createdAt', 'desc');
				return result;
			})
		);
	}

	public createBriefcase(briefcase: BriefcaseJob): Observable<any> {
		return this.makeHttpRequest('post', `api2/briefcase`, briefcase);

	}
	public delete(params: BriefcaseDeleteParams): Observable<void> {
		if (params?.id) {
			return this.makeHttpRequest('delete', `api2/briefcase/${params.id}`);
		}
		return of(null);
	}

	public put(params: BriefcasePostParams): Observable<void> {
		if (params?.briefcase) {
			const target = briefcaseToBriefcaseJobResourceMapper.map(params?.briefcase);
			return this.makeHttpRequest('put', `api2/briefcase/${params.briefcase.id}`, target);
		}
		return of(null);
	}

	public download(params: BriefcaseDownloadParams): Observable<string> {
		if (params?.file) {
			const mapper = new FileToDownloadBriefcaseFileRequestResourceMapper();
			return this.makeHttpRequest<FileDownloadDetailsResource>('post', `api2/briefcase/download`, mapper.map(params.file)).pipe(map((response) => response.url));
		}

		if (params?.type && params?.briefcaseJobId) {
			const mapper = new FileTypeToFileTypeResourceMapper();
			const body = {
				fileType: mapper.map(params.type),
				briefcaseJobId: params.briefcaseJobId
			} as DownloadBriefcaseFileRequestResource;
			return this.makeHttpRequest<FileDownloadDetailsResource>('post', `api2/briefcase/download`, body).pipe(map((response) => response.url));
		}
		return of(null);
	}

	public createFile(params: CreateFileParams): Observable<void> {
		if (params?.id && params?.type) {
			const mapper = new FileTypeToFileTypeResourceMapper();
			return this.makeHttpRequest('post', `api2/briefcase/create`, {
				briefcaseJobId: params?.id,
				fileType: mapper.map(params?.type)
			});
		}
		return of(null);
	}

	public getFileList(params: GetFileListParams): Observable<FileRecord[]> {
		if (params?.id) {
			return this.makeHttpRequest<GetBriefcaseFilesResponseResource>('get', `api2/briefcase/files/${ params?.id }`).pipe(
				map((response) => {
					return publicFileResourceToFileMapper.mapCollection(response.files);
				})
			);
		}
		return of(null);
	}
}

interface BriefcaseGetParams {
	top?: number;
	skip?: number;
	id?: string;
}

interface BriefcaseDeleteParams {
	id: string;
}

interface BriefcasePostParams {
	briefcase: Briefcase;
}

interface BriefcaseDownloadParams {
	file?: FileRecord;
	type?: FileType;
	briefcaseJobId?: string;
}

interface CreateFileParams {
	id: string;
	type: FileType;
}

interface GetFileListParams {
	id: string;
}
